/**
 * approved_vs_invoiced — every vertical. GET /dashboard/approved-vs-invoiced
 * (api#1125): work approved in the period (the same tenant-zone window as
 * Revenue Pulse), split by whether a live invoice carries it. Month by default.
 *
 * Three figures, each the API's `total`, per currency and never summed across
 * them. The API holds approved == invoiced + not invoiced in every currency;
 * this screen adds nothing up, so it cannot disagree with that.
 *
 * A vertical that is not `ok` is named under the figures (MoneyVerticalNotes):
 * its unpriced rows, a figure that cannot be stated, or a failed read. With
 * nothing countable and a vertical missing, a figure is —, not $0.00.
 */
import { fmtCurrency } from '../../../lib/display-labels';
import { fmtAmounts } from '../../../lib/currency-amounts';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import type { ApprovedVsInvoiced } from '../../../types/overview-money';
import { LoadFailureNotice } from '../../ui/LoadFailureNotice';
import { MoneyVerticalNotes } from '../MoneyVerticalNotes';
import { PeriodToggle, usePeriod } from '../PeriodToggle';
import { WidgetFigure, WidgetPanel, WidgetPending } from '../WidgetPanel';
import { useApprovedVsInvoiced } from '../overviewQueries';

const PERIOD_KEY = 'approvedVsInvoicedPeriod';

export function ApprovedVsInvoicedFigures({ read }: { read: ApprovedVsInvoiced }) {
  // Empty is a real $0.00 only when every vertical was counted.
  const none = read.complete ? fmtCurrency(0) : '—';
  return (
    <>
      <div data-testid="avi-grid" className="grid grid-cols-1 gap-y-4 sm:grid-cols-3 sm:gap-x-4">
        <WidgetFigure testId="avi-approved" label="Approved" value={fmtAmounts(read.total.approved, none)} />
        <WidgetFigure testId="avi-invoiced" label="Invoiced" value={fmtAmounts(read.total.invoiced, none)} />
        <WidgetFigure testId="avi-not-invoiced" label="Not invoiced" value={fmtAmounts(read.total.not_invoiced, none)} />
      </div>
      <MoneyVerticalNotes verticals={read.verticals} testId="avi-vertical-notes" />
    </>
  );
}

export default function ApprovedVsInvoicedWidget() {
  const [period, choose] = usePeriod(PERIOD_KEY, 'month');
  const q = useApprovedVsInvoiced(period);
  const failure = q.data === undefined ? queryLoadFailure(q) : null;

  return (
    <WidgetPanel
      widgetKey="approved_vs_invoiced"
      eyebrow={`Approved this ${period}`}
      title="Approved vs Invoiced"
      aside={<PeriodToggle period={period} onChange={choose} />}
    >
      <div className="p-4">
        {failure ? (
          <LoadFailureNotice error={queryFailureError(q)} kind={failure} subject="approved and invoiced work" className="py-6" allowed="admins, office staff, supervisors and dispatchers" />
        ) : q.data === undefined ? (
          <WidgetPending rows={2} />
        ) : q.data === null ? (
          <LoadFailureNotice error={new Error('No approved-vs-invoiced payload')} kind="unreachable" subject="approved and invoiced work" className="py-6" />
        ) : (
          <ApprovedVsInvoicedFigures read={q.data} />
        )}
      </div>
    </WidgetPanel>
  );
}
